import PropTypes from 'prop-types';
import React from 'react';

/**
 * AdminNodeBelt - Unified Horizontal Navigation Component
 * Standardizes the "belt" of interactive nodes used across Admin modules and Dashboards.
 * 
 * @param {Array} items - List of navigation nodes {id, label, subLabel, icon, count, active, onClick, activeClass, iconColor, activeIconColor}
 */
const AdminNodeBelt = ({ items, className = '' }) => {
    if (!items || items.length === 0) return null;

    return (
        <div className={`flex shrink-0 items-center gap-3 md:gap-4 overflow-x-auto overflow-y-visible h-14 md:h-20 min-h-[56px] md:min-h-[80px] scrollbar-premium animate-in fade-in duration-700 bg-[#F8F9FA] md:bg-transparent border-b md:border-none border-slate-200 pt-0 pb-2 ${className}`}>
            {items.map(item => (
                <button
                    key={item.id}
                    onClick={item.onClick}
                    className={`flex items-center gap-2 md:gap-4 px-3 md:px-6 h-10 md:h-16 rounded-xl md:rounded-2xl border transition-all whitespace-nowrap active:scale-95 group relative shadow-md ${item.active
                        ? (item.activeClass || 'bg-primary border-primary text-white ')
                        : 'bg-white border-transparent text-slate-500 hover:bg-slate-50'
                        }`}
                >
                    {/* Icon / Counter Node - Hidden on Mobile */}
                    <div className={`hidden md:flex w-8 md:w-10 h-8 md:h-10 rounded-lg md:rounded-xl items-center justify-center transition-all ${item.active
                        ? (item.activeIconColor || 'bg-white/20 text-accent')
                        : `bg-slate-50 ${item.iconColor || 'text-slate-400'} group-hover:scale-110`
                        }`}>
                        {item.count !== undefined && !item.icon ? (
                            <span className="text-[10px] md:text-xs font-black">{item.count}</span>
                        ) : (
                            <item.icon size={16} md:size={18} strokeWidth={2.5} />
                        )}
                    </div>

                    {/* Text Node */}
                    <div className="flex flex-col items-start">
                        <span className={`text-[11px] font-black uppercase tracking-wider italic leading-tight ${item.active ? 'text-white' : 'text-primary'}`}>
                            <span className="md:hidden">{item.mobileLabel || item.label}</span>
                            <span className="hidden md:inline">{item.label}</span>
                        </span>
                        <span className={`text-[8px] font-bold uppercase tracking-widest leading-none mt-1 ${item.active ? 'text-white/60' : 'text-slate-400'}`}>
                            {item.subLabel}
                        </span>
                    </div>

                    {/* Active Accent Indicator */}
                    {item.active && (
                        <div className="absolute left-0 top-4 bottom-4 w-1 bg-accent rounded-r-full shadow-[0_0_10px_rgba(255,200,0,0.5)]" />
                    )}
                </button>
            ))}
        </div>
    );
};

AdminNodeBelt.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        subLabel: PropTypes.string,
        icon: PropTypes.elementType,
        count: PropTypes.number,
        active: PropTypes.bool,
        onClick: PropTypes.func,
        activeClass: PropTypes.string,
        iconColor: PropTypes.string,
        activeIconColor: PropTypes.string
    }))
};

export default React.memo(AdminNodeBelt);
